import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Column, DataTable } from "@/components/ui/data-table";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";
import { formatDateTime } from "@/utils/dateUtils";
import { useRooms } from "@/hooks/useRooms";
import {
  PartnerOrderEarningState,
  PartnerOrderRecord,
  PartnerOrdersCatalogFilter,
} from "@/types/partner-order";

/** "all" | "hotel_wide" | a roomId — one control for the two query params. */
export const PARTNER_ROOM_FILTER_ALL = "all";
export const PARTNER_ROOM_FILTER_HOTEL_WIDE = "hotel_wide";

export function decodePartnerRoomFilter(value: string): {
  attribution?: "room" | "hotel_wide";
  roomId?: string;
} {
  if (value === PARTNER_ROOM_FILTER_ALL) {
    return {};
  }
  if (value === PARTNER_ROOM_FILTER_HOTEL_WIDE) {
    return { attribution: PARTNER_ROOM_FILTER_HOTEL_WIDE };
  }
  return { attribution: "room", roomId: value };
}

interface PartnerOrdersTableProps {
  orders: PartnerOrderRecord[];
  total: number;
  loading: boolean;
  /** True on any fetch failure — the table shows a fixed friendly message,
   * never the response body (sim found a raw error JSON leak, 2026-09-27). */
  error?: boolean;
  page: number;
  limit: number;
  onPageChange: (page: number) => void;
  catalog: PartnerOrdersCatalogFilter;
  onCatalogChange: (value: PartnerOrdersCatalogFilter) => void;
  showHotelColumn?: boolean;
  /**
   * hotelId -> hotel name, sourced from the page's already-loaded hotel
   * list. Orders don't reliably carry a display name — falls back to "—",
   * never the raw id (sim found the raw id shown, 2026-09-27).
   */
  hotelNamesById?: Map<string, string>;
  /** Off at hotel scope — the API omits hotelFeeAmount/hotelFeePercent there. */
  showCommission?: boolean;
  /**
   * Task 0041 — the room filter needs a single hotel's room list
   * (`useRooms`), so it's only offered on single-hotel surfaces: pass
   * `hotelId` + `roomFilter`/`onRoomFilterChange` there. Omit all three on a
   * multi-hotel table (the tenant-wide list, the admin summary) — the Room
   * column still renders from each order's own `roomNumber`/
   * `attributionSource` either way.
   */
  hotelId?: string;
  roomFilter?: string;
  onRoomFilterChange?: (value: string) => void;
}

// Null-safe: a missing/malformed amount must render as "—", never throw
// (sim hit a white screen from `undefined.toLocaleString()`, 2026-09-27).
// No currency field on the order record — this app's partner-money
// convention is EUR everywhere else, so that's the fixed display currency.
function formatOrderAmount(value: number | null | undefined): string {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return "—";
  }
  return `€${value.toLocaleString("en-GB")}`;
}

/**
 * Task 0031, contract 2026-09-29: an order the partner never returned an
 * exact amount for (`amountEstimated`) shows every one of its money
 * columns as approximate, with a tooltip explaining why — never a bare
 * number that looks as exact as any other row.
 */
function OrderAmountCell({
  value,
  estimated,
  t,
}: {
  value: number | null | undefined;
  estimated: boolean;
  t: (key: string) => string;
}): React.JSX.Element {
  const formatted = formatOrderAmount(value);
  if (!estimated || formatted === "—") {
    return <span>{formatted}</span>;
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="cursor-help underline decoration-dotted">
          ≈ {formatted}
        </span>
      </TooltipTrigger>
      <TooltipContent>
        {t("billing.partnerOrders.estimatedTooltip")}
      </TooltipContent>
    </Tooltip>
  );
}

// Currency-aware, mirrors `GuesteriaPartnersRevenueStats.getCurrencySymbol`.
// `providerCommissionAmount` carries its own currency (the Viator report's),
// separate from this app's fixed EUR partner-money convention.
function formatProviderCommission(
  value: number | null | undefined,
  currency: string | undefined,
): string {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return "—";
  }
  let symbol = "";
  if (currency === "EUR") {
    symbol = "€";
  } else if (currency === "USD") {
    symbol = "$";
  } else if (currency === "GBP") {
    symbol = "£";
  } else if (currency) {
    symbol = `${currency} `;
  }
  return `${symbol}${value.toLocaleString("en-GB")}`;
}

/** Task 0033, contract 2026-09-29 — earned/pending/cancelled, admin sees the reconciliation columns. */
function EarningStateBadge({
  state,
  t,
}: {
  state: PartnerOrderEarningState;
  t: (key: string) => string;
}): React.JSX.Element {
  let variant: "success" | "destructive" | "outline" = "outline";
  if (state === "earned") {
    variant = "success";
  } else if (state === "cancelled") {
    variant = "destructive";
  }
  return (
    <Badge variant={variant}>
      {t(`billing.partnerOrders.earningStateValues.${state}`)}
    </Badge>
  );
}

function RoomAttributionCell({
  row,
  t,
}: {
  row: PartnerOrderRecord;
  t: (key: string) => string;
}): React.JSX.Element {
  if (row.attributionSource === "hotel_wide") {
    return <span>{t("billing.partnerOrders.receptionLabel")}</span>;
  }
  if (row.roomNumber) {
    return <span>{row.roomNumber}</span>;
  }
  if (row.attributionSource === "room") {
    return (
      <span className="text-muted-foreground">
        {t("partners.detail.attribution.unknownRoomLabel")}
      </span>
    );
  }
  // No attribution data at all — an order from before this contract.
  return <span>—</span>;
}

const PartnerOrdersTableInner: React.FC<PartnerOrdersTableProps> = ({
  orders,
  total,
  loading,
  error = false,
  page,
  limit,
  onPageChange,
  catalog,
  onCatalogChange,
  showHotelColumn = false,
  hotelNamesById,
  showCommission = true,
  hotelId,
  roomFilter,
  onRoomFilterChange,
}) => {
  const { t } = useTranslation();

  const showRoomFilter = Boolean(hotelId && onRoomFilterChange);
  const { data: roomsResponse } = useRooms(
    hotelId || "",
    null,
    null,
    undefined,
    showRoomFilter,
  );
  const rooms = useMemo(() => roomsResponse?.data ?? [], [roomsResponse]);

  const totalPages = Math.max(1, Math.ceil(total / Math.max(limit, 1)));

  const columns: Column<PartnerOrderRecord>[] = [
    {
      key: "travelDate",
      header: t("billing.partnerOrders.columns.serviceDate"),
      render: (value) => (
        <span className="text-sm">{formatDateTime(value)}</span>
      ),
    },
    {
      key: "bookingDate",
      header: t("billing.partnerOrders.columns.bookedOn"),
      render: (value) => (
        <span className="text-sm">{formatDateTime(value)}</span>
      ),
    },
    {
      key: "createdAt",
      header: t("billing.partnerOrders.columns.date"),
      render: (value) => (
        <span className="text-sm">{formatDateTime(value)}</span>
      ),
    },
    ...(showHotelColumn
      ? [
          {
            key: "hotelName",
            header: t("billing.partnerOrders.columns.hotel"),
            render: (value: string | undefined, row: PartnerOrderRecord) =>
              value || hotelNamesById?.get(row.hotelId) || "—",
          } as Column<PartnerOrderRecord>,
        ]
      : []),
    {
      key: "partnerProductName",
      header: t("billing.partnerOrders.columns.product"),
      className: "min-w-[220px] whitespace-normal",
      render: (value: string, row) =>
        !row.outOfCatalog && row.serviceId ? (
          <Link
            to={`/services/${row.serviceId}`}
            className="text-primary hover:underline"
          >
            {value}
          </Link>
        ) : (
          <span>{value}</span>
        ),
    },
    {
      key: "outOfCatalog",
      header: t("billing.partnerOrders.columns.catalog"),
      render: (value: boolean) =>
        value ? (
          <Badge variant="new_secondary">
            {t("billing.partnerOrders.outsideCatalogBadge")}
          </Badge>
        ) : (
          <Badge variant="new_default">
            {t("billing.partnerOrders.inCatalogBadge")}
          </Badge>
        ),
    },
    {
      key: "roomNumber",
      header: t("billing.partnerOrders.columns.room"),
      render: (_value: string | null | undefined, row: PartnerOrderRecord) => (
        <RoomAttributionCell row={row} t={t} />
      ),
    },
    {
      key: "travellers",
      header: t("billing.partnerOrders.columns.guests"),
      render: (value: number) => <span className="text-sm">{value}</span>,
    },
    {
      key: "earningState",
      header: t("billing.partnerOrders.columns.earningState"),
      render: (value: PartnerOrderEarningState) => (
        <EarningStateBadge state={value} t={t} />
      ),
    },
    {
      key: "totalGuestPrice",
      header: t("billing.partnerOrders.columns.amount"),
      render: (value: number | null | undefined, row) => (
        <OrderAmountCell
          value={value}
          estimated={Boolean(row.amountEstimated)}
          t={t}
        />
      ),
    },
    ...(showCommission
      ? [
          {
            key: "hotelFeeAmount",
            header: t("billing.partnerOrders.columns.commission"),
            render: (value: number | null | undefined, row) => (
              <OrderAmountCell
                value={value}
                estimated={Boolean(row.amountEstimated)}
                t={t}
              />
            ),
          } as Column<PartnerOrderRecord>,
        ]
      : []),
    ...(showCommission
      ? [
          {
            key: "providerCommissionAmount",
            header: t("billing.partnerOrders.columns.providerCommission"),
            render: (value: number | null | undefined, row) => (
              <span>
                {formatProviderCommission(
                  value,
                  row.providerCommissionCurrency,
                )}
              </span>
            ),
          } as Column<PartnerOrderRecord>,
          {
            key: "reportImportedAt",
            header: t("billing.partnerOrders.columns.reportImportedAt"),
            render: (value: string | null | undefined) => (
              <span className="text-sm">
                {value ? formatDateTime(value) : "—"}
              </span>
            ),
          } as Column<PartnerOrderRecord>,
        ]
      : []),
  ];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-end gap-2">
        {showRoomFilter ? (
          <Select value={roomFilter} onValueChange={onRoomFilterChange}>
            <SelectTrigger size="sm" className="h-8 w-[180px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={PARTNER_ROOM_FILTER_ALL}>
                {t("billing.partnerOrders.roomFilter.all")}
              </SelectItem>
              <SelectItem value={PARTNER_ROOM_FILTER_HOTEL_WIDE}>
                {t("billing.partnerOrders.receptionLabel")}
              </SelectItem>
              {rooms.map((room) => (
                <SelectItem key={room.id} value={room.id}>
                  {room.number}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : null}
        <Select
          value={catalog}
          onValueChange={(value) =>
            onCatalogChange(value as PartnerOrdersCatalogFilter)
          }
        >
          <SelectTrigger size="sm" className="h-8 w-[180px] text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              {t("billing.partnerOrders.filter.all")}
            </SelectItem>
            <SelectItem value="in">
              {t("billing.partnerOrders.filter.inCatalog")}
            </SelectItem>
            <SelectItem value="out">
              {t("billing.partnerOrders.filter.outsideCatalog")}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {error ? (
        <p className="text-destructive py-6 text-center text-sm">
          {t("billing.partnerOrders.loadError")}
        </p>
      ) : (
        <DataTable
          data={orders}
          columns={columns}
          loading={loading}
          getRowKey={(row) => row.id}
          emptyMessage={t("billing.partnerOrders.empty")}
          withSorting={false}
          pagination={{ currentPage: page, totalPages, onPageChange }}
        />
      )}
    </div>
  );
};

export const PartnerOrdersTable: React.FC<PartnerOrdersTableProps> = (
  props,
) => {
  const { t } = useTranslation();

  return (
    <ErrorBoundary
      fallback={
        <p className="text-destructive py-6 text-center text-sm">
          {t("billing.partnerOrders.loadError")}
        </p>
      }
    >
      <PartnerOrdersTableInner {...props} />
    </ErrorBoundary>
  );
};
